






























import { computed, defineComponent, onBeforeUnmount, ref } from "@vue/composition-api";
import { throttle } from "lodash-es";
import { Location } from "vue-router";
import { useAnnouncements } from "@/hooks/useAnnouncements";
import useAuthUser from "@/hooks/useAuthUser";
import providers from "@/lib/api";
import emitter from "@/lib/emitter";
import { $t } from "@/lib/i18nHelpers";
import { MediaFilesController } from "@/lib/MediaFilesController";
import { getProfileUrl } from "@/lib/urlHelpers";
import { EVENTS } from "@/const";
import BaseAvatar from "@/components/BaseAvatar";
import BaseIcon from "@/components/BaseIcon";

interface MenuItem {
  label: string;
  active?: string;
  name: string;
  to: Location;
  hasNew?: boolean;
  exact?: boolean;
  staticClass?: string;
}

export default defineComponent({
  name: "MainNavigationBar",
  components: { BaseIcon, BaseAvatar },
  setup() {
    const { authUser } = useAuthUser();
    const { hasUnreadAnnouncement, getUnreadAnnouncement } = useAnnouncements();

    const unreadMessageThreadsCount = ref(0);

    const profileTo = computed(() => (authUser.value ? getProfileUrl(authUser.value) : { name: "home" }));
    const hasNewMessages = computed(() => unreadMessageThreadsCount.value > 0);
    const avatarUrl = computed(() => {
      if (!authUser.value?.avatar) return "";
      return MediaFilesController.getImageThumbnail(authUser.value.avatar.urlPart, 76);
    });
    const menuList = computed<MenuItem[]>(() => {
      if (!authUser.value) return [];

      return [
        {
          label: "sci-home-outline",
          active: "sci-home",
          name: $t("Home"),
          to: {
            name: "home",
          },
        },
        {
          label: "sci-account-circle-outline",
          active: "sci-account-circle",
          name: $t("Profile"),
          to: profileTo.value,
          exact: true,
        },
        {
          label: "sci-chat-outline",
          active: "sci-chat",
          name: $t("Messages"),
          to: {
            name: "messageThreads",
          },
          hasNew: hasNewMessages.value || hasUnreadAnnouncement.value,
          staticClass: hasUnreadAnnouncement.value && !hasNewMessages.value ? "__red-dot" : "",
        },
        {
          label: "sci-bell-outline",
          active: "sci-bell",
          name: $t("Notifications"),
          to: {
            name: "notifications",
          },
        },
        {
          label: "sci-menu",
          name: $t("More"),
          to: {
            name: "more",
          },
        },
      ];
    });

    const getUnreadMessagesCount = async () => {
      unreadMessageThreadsCount.value = await providers.MessageThreadsProvider.getUnreadCount();
    };

    const receivedMessage = throttle(getUnreadMessagesCount, 3000, { trailing: false });

    const hideMenuHandler = (hide: boolean = false) => {
      const root = document.documentElement;
      root.style.setProperty("--safe-bottom-position", hide ? "0px" : "calc(56px + env(safe-area-inset-bottom))");
    };

    const created = async () => {
      hideMenuHandler();
      await Promise.all([getUnreadMessagesCount(), getUnreadAnnouncement()]);
    };

    created();
    emitter.on<void>(EVENTS.MESSAGES_NEW_MESSAGE, receivedMessage);
    emitter.on<void>(EVENTS.MESSAGES_MARKED_READ, getUnreadMessagesCount);
    emitter.on<void>(EVENTS.MESSAGES_UPDATE_MESSAGE_UNREAD_COUNT, getUnreadMessagesCount);
    emitter.on<void>(EVENTS.ANNOUNCEMENT_NEW_ANNOUNCEMENT, getUnreadAnnouncement);
    emitter.on<boolean>(EVENTS.MAIN_MENU_HIDE, hideMenuHandler);

    onBeforeUnmount(() => {
      hideMenuHandler(true);
      emitter.off<void>(EVENTS.MESSAGES_NEW_MESSAGE, receivedMessage);
      emitter.off<void>(EVENTS.MESSAGES_MARKED_READ, getUnreadMessagesCount);
      emitter.off<void>(EVENTS.MESSAGES_UPDATE_MESSAGE_UNREAD_COUNT, getUnreadMessagesCount);
      emitter.off<void>(EVENTS.ANNOUNCEMENT_NEW_ANNOUNCEMENT, getUnreadAnnouncement);
      emitter.off<boolean>(EVENTS.MAIN_MENU_HIDE, hideMenuHandler);
    });

    return { avatarUrl, menuList, profileTo, authUser };
  },
});
